import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import AuthInitializer from './components/AuthInitializer';
import ProtectedRoute from './components/ProtectedRoute';
import PortalLayout from './components/portal/PortalLayout';
import { CmmsAuthProvider } from './contexts/CmmsAuthContext';
import { CompanyProvider } from './contexts/CompanyContext';
import Dashboard from './pages/portal/Dashboard';

// CMMS Pages
import CMSSDashboard from './pages/portal/cmms/CMSSDashboard';
import Assets from './pages/portal/cmms/Assets';
import WorkOrders from './pages/portal/cmms/WorkOrders';
import Maintenance from './pages/portal/cmms/Maintenance';
import Inventory from './pages/portal/cmms/Inventory';
import Reports from './pages/portal/cmms/Reports';
import CmmsPlans from './pages/portal/cmms/Plans';
import CmmsTeam from './pages/portal/cmms/Team';
import Suppliers from './pages/portal/cmms/Suppliers';
import Contracts from './pages/portal/cmms/Contracts';
import Visits from './pages/portal/cmms/Visits';
import ServiceReports from './pages/portal/cmms/ServiceReports';
import Branches from './pages/portal/cmms/Branches';
import BranchDetail from './pages/portal/cmms/BranchDetail';
import Locations from './pages/portal/cmms/Locations';
import Warehouses from './pages/portal/cmms/Warehouses';
import PMSchedules from './pages/portal/cmms/PMSchedules';
import AssetDetail from './pages/portal/cmms/AssetDetail';
import WorkOrderDetail from './pages/portal/cmms/WorkOrderDetail';

// Chatbot Pages
import ChatbotDashboard from './pages/portal/chatbot/ChatbotDashboard';
import ManageChatbots from './pages/portal/chatbot/ManageChatbots';
import Conversations from './pages/portal/chatbot/Conversations';
import Analytics from './pages/portal/chatbot/Analytics';
import ChatbotPlans from './pages/portal/chatbot/Plans';
import AgentManagement from './pages/portal/chatbot/AgentManagement';
import LiveInbox from './pages/portal/chatbot/LiveInbox';
import AgentAnalytics from './pages/portal/chatbot/AgentAnalytics';

// Account Pages
import AccountSettings from './pages/portal/account/AccountSettings';
import Notifications from './pages/portal/account/Notifications';

// Other Pages
import Products from './pages/portal/Products';
import Billing from './pages/portal/Billing';
import BillingSuccess from './pages/portal/BillingSuccess';
import Checkout from './pages/portal/Checkout';
import Support from './pages/portal/Support';
import TicketDetail from './pages/portal/TicketDetail';
import ActivityLog from './pages/portal/ActivityLog';
import HelpCenter from './pages/portal/help/HelpCenter';
import ChatbotHelpPage from './pages/portal/help/ChatbotHelpPage';
import CmmsHelpPage from './pages/portal/help/CmmsHelpPage';

// Redirects the portal index route based on the user's companyRole and subscriptions.
function PortalIndex() {
  const companyRole = localStorage.getItem('companyRole');
  const hasChatbot = localStorage.getItem('hasChatbotSubscription') === 'true';
  const hasCmms = localStorage.getItem('hasCmmsSubscription') === 'true';

  if (companyRole === 'CHAT_AGENT') return <Navigate to="/portal/chatbot/inbox" replace />;
  if (companyRole === 'CHAT_SUPERVISOR') return <Navigate to="/portal/chatbot" replace />;
  if (!hasChatbot && hasCmms) return <Navigate to="/portal/cmms" replace />;
  return <Dashboard />;
}

function App() {
  return (
    <AuthInitializer>
      <CompanyProvider>
      <CmmsAuthProvider>
        <BrowserRouter>
          <Routes>
          {/* Redirect root to portal */}
          <Route path="/" element={<Navigate to="/portal" replace />} />
        
        {/* Protected Portal Routes */}
        <Route
          path="/portal"
          element={
            <ProtectedRoute>
              <PortalLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<PortalIndex />} />
          
          {/* CMMS Routes */}
          <Route path="cmms" element={<CMSSDashboard />} />
          <Route path="cmms/assets" element={<Assets />} />
          <Route path="cmms/assets/:id" element={<AssetDetail />} />
          <Route path="cmms/work-orders" element={<WorkOrders />} />
          <Route path="cmms/work-orders/:id" element={<WorkOrderDetail />} />
          <Route path="cmms/maintenance" element={<Maintenance />} />
          <Route path="cmms/inventory" element={<Inventory />} />
          <Route path="cmms/reports" element={<Reports />} />
          <Route path="cmms/team" element={<CmmsTeam />} />
          <Route path="cmms/plans" element={<CmmsPlans />} />
          <Route path="cmms/suppliers" element={<Suppliers />} />
          <Route path="cmms/contracts" element={<Contracts />} />
          <Route path="cmms/visits" element={<Visits />} />
          <Route path="cmms/service-reports" element={<ServiceReports />} />
          <Route path="cmms/branches" element={<Branches />} />
          <Route path="cmms/branches/:id" element={<BranchDetail />} />
          <Route path="cmms/locations" element={<Locations />} />
          <Route path="cmms/warehouses" element={<Warehouses />} />
          <Route path="cmms/pm-schedules" element={<PMSchedules />} />

          {/* Chatbot Routes */}
          <Route path="chatbot" element={<ChatbotDashboard />} />
          <Route path="chatbot/manage" element={<ManageChatbots />} />
          <Route path="chatbot/conversations" element={<Conversations />} />
          <Route path="chatbot/analytics" element={<Analytics />} />
          <Route path="chatbot/plans" element={<ChatbotPlans />} />
          <Route path="chatbot/agents" element={<AgentManagement />} />
          <Route path="chatbot/inbox" element={<LiveInbox />} />
          <Route path="chatbot/agent-analytics" element={<AgentAnalytics />} />
          
          {/* Account Routes */}
          <Route path="account/settings" element={<AccountSettings />} />
          <Route path="account/notifications" element={<Notifications />} />
          
          {/* Products & Other Routes */}
          <Route path="products" element={<Products />} />
          <Route path="billing" element={<Billing />} />
          <Route path="billing/success" element={<BillingSuccess />} />
          <Route path="checkout" element={<Checkout />} />
          <Route path="support" element={<Support />} />
          <Route path="support/:id" element={<TicketDetail />} />
          <Route path="activity" element={<ActivityLog />} />
          <Route path="help" element={<HelpCenter />} />
          <Route path="help/chatbot" element={<ChatbotHelpPage />} />
          <Route path="help/cmms" element={<CmmsHelpPage />} />
        </Route>

          {/* 404 */}
          <Route path="*" element={<Navigate to="/portal" replace />} />
        </Routes>
      </BrowserRouter>
      </CmmsAuthProvider>
      </CompanyProvider>
    </AuthInitializer>
  );
}

export default App;